import React, { useRef, useState } from 'react';
import {
  Datagrid,
  TextField,
  DateField,
  useRecordContext,
  useRefresh,
} from 'react-admin';

import Chip from '@material-ui/core/Chip';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import ReplayIcon from '@material-ui/icons/Replay';
import VisibilityIcon from '@material-ui/icons/Visibility';

import { DialogoConfirmacao } from '../../Components';
import ActionsList from '../../Components/ReactAdmin/ActionsList';
import ActionsField from '../../Components/ReactAdmin/Fields/ActionsField';
import ListPageComum from '../../Components/ReactAdmin/List/ListPageComum';
import { ProtegerPagina } from '../../layout/VerificaPermissaoPage';
import { PRIMARY_COLOR } from '../../utils';
import {
  GERAR_INGESTAO_PERIODO_HABILITADA,
  GERAR_PACOTE_FISCAL_HABILITADO,
} from '../../utils/constants';
import { PERMISSAO_TELA_RELATORIOS_FISCAIS_LISTAR } from '../../utils/dadosTelas';
import {
  podeBaixar,
  podeCancelar,
  podeReprocessar,
  useAcoesExecucao,
  usePermissoesRelatoriosFiscais,
} from './acoes';
import DialogoGerar from './DialogoGerar';
import DialogoShow from './DialogoShow';
import Filter from './Filter';

const useStyles = makeStyles(() => ({
  dataHeader: {
    flex: 1,
    backgroundColor: PRIMARY_COLOR,
    padding: '10px',
    color: 'white',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(224, 224, 224, 1)',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
  dataCell: {
    flex: 1,
    padding: '10px',
    fontWeight: 500,
    lineHeight: '1.5rem',
    fontSize: '0.875rem',
    borderSpacing: 0,
    borderCollapse: 'collapse',
  },
}));

const CORES_STATUS = {
  pendente: '#9e9e9e',
  processando: '#1976d2',
  sucesso: '#2e7d32',
  sucesso_com_pendencias: '#ed6c02',
  erro: '#d32f2f',
  cancelada: '#616161',
};

const NOMES_STATUS = {
  pendente: 'Aguardando',
  processando: 'Processando',
  sucesso: 'Concluído',
  sucesso_com_pendencias: 'Concluído com pendências',
  erro: 'Erro',
  cancelada: 'Cancelada',
};

const NOMES_TIPO = {
  ingestao: 'Ingestão',
  pacote_mensal: 'Pacote mensal',
};

const NOMES_ORIGEM = {
  agendada: 'Agendada',
  manual: 'Manual',
  reprocessamento: 'Reprocessamento',
};

// No react-admin 3.11, useRecordContext(props) devolve o próprio `props` quando
// não há RecordContext (ex.: dentro do Datagrid), e o registro fica em .record.
const useRegistro = (props) => {
  const ctx = useRecordContext(props);
  return (ctx && ctx.record) || null;
};

const StatusField = (props) => {
  const record = useRegistro(props);
  if (!record) return null;
  return (
    <Chip
      size="small"
      label={NOMES_STATUS[record.status] || record.status}
      style={{
        backgroundColor: CORES_STATUS[record.status] || '#9e9e9e',
        color: 'white',
        fontWeight: 600,
      }}
    />
  );
};

// Tipo e competência/janela juntos: "Pacote mensal" com "09/2026" embaixo, ou
// "Ingestão" com "01/10/2026 a 03/10/2026".
const TipoField = (props) => {
  const record = useRegistro(props);
  if (!record) return null;

  let janela;
  if (record.tipo === 'pacote_mensal') {
    janela = `Competência ${String(record.competencia_mes).padStart(2, '0')}/${
      record.competencia_ano
    }`;
  } else {
    const formatar = (data) =>
      data ? new Date(data).toLocaleDateString('pt-BR') : '-';
    janela = `${formatar(record.data_inicio)} a ${formatar(record.data_fim)}`;
  }

  return (
    <div>
      <div style={{ fontWeight: 600 }}>
        {NOMES_TIPO[record.tipo] || record.tipo}
      </div>
      <div style={{ color: '#757575', fontSize: '0.8rem' }}>{janela}</div>
    </div>
  );
};

const OrigemField = (props) => {
  const record = useRegistro(props);
  if (!record) return null;
  return <span>{NOMES_ORIGEM[record.origem] || record.origem}</span>;
};

const DuracaoField = (props) => {
  const record = useRegistro(props);
  if (!record || !record.duracao_ms) return <span>-</span>;
  const segundos = Math.round(record.duracao_ms / 1000);
  if (segundos < 60) return <span>{`${segundos}s`}</span>;
  const minutos = Math.floor(segundos / 60);
  return <span>{`${minutos}min ${segundos % 60}s`}</span>;
};

const List = (props) => {
  const refDialogoShow = useRef(null);
  const classes = useStyles();
  const refresh = useRefresh();
  const { podeGerar } = usePermissoesRelatoriosFiscais();
  const [dialogoGerarAberto, setDialogoGerarAberto] = useState(false);

  function handleShow(id) {
    if (refDialogoShow.current) refDialogoShow.current.handleOpen(id);
  }

  const {
    refDialogoConfirmacao,
    pedirReprocessar,
    pedirCancelar,
    handleConfirma,
    baixar,
  } = useAcoesExecucao({
    onAtualizado: refresh,
    onCriada: (id) => {
      refresh();
      handleShow(id);
    },
  });

  const toolbarActions = (
    <ActionsList
      {...props}
      actions={
        podeGerar &&
        (GERAR_PACOTE_FISCAL_HABILITADO || GERAR_INGESTAO_PERIODO_HABILITADA)
          ? [
              {
                label: 'Gerar',
                icon: <AddIcon />,
                onClick: () => setDialogoGerarAberto(true),
              },
            ]
          : []
      }
    />
  );

  const renderGrid = () => (
    <>
      <Datagrid {...props}>
        <TextField
          source="id"
          label="#"
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <TipoField
          label="Execução"
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <OrigemField
          label="Origem"
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <StatusField
          label="Status"
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <DateField
          source="iniciado_em"
          label="Iniciada em"
          showTime
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <DuracaoField
          label="Duração"
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          sortable={false}
        />
        <ActionsField
          textAlign="right"
          label="Ações"
          sortable={false}
          headerClassName={classes.dataHeader}
          cellClassName={classes.dataCell}
          actions={[
            {
              title: 'Ver',
              icon: <VisibilityIcon />,
              onClick: (r) => handleShow(r.id),
            },
            {
              title: 'Baixar',
              icon: <GetAppIcon />,
              show: (r) => podeBaixar(r),
              onClick: (r) => baixar(r),
            },
            {
              title: 'Reprocessar',
              icon: <ReplayIcon />,
              show: (r) => podeGerar && podeReprocessar(r),
              onClick: (r) => pedirReprocessar(r),
            },
            {
              title: 'Cancelar',
              icon: <CancelOutlinedIcon />,
              show: (r) => podeGerar && podeCancelar(r),
              onClick: (r) => pedirCancelar(r),
            },
          ]}
        />
      </Datagrid>
      <DialogoShow ref={refDialogoShow} />
      <DialogoConfirmacao
        ref={refDialogoConfirmacao}
        handleConfirma={handleConfirma}
      />
      <DialogoGerar
        open={dialogoGerarAberto}
        onClose={() => setDialogoGerarAberto(false)}
        onCriada={(id) => {
          refresh();
          handleShow(id);
        }}
      />
    </>
  );

  return (
    <ProtegerPagina permissao={PERMISSAO_TELA_RELATORIOS_FISCAIS_LISTAR}>
      <ListPageComum
        title="Relatórios Fiscais — Histórico"
        filters={<Filter />}
        toolbarActions={toolbarActions}
        renderGrid={renderGrid}
        perPage={50}
        listProps={{ sort: { field: 'id', order: 'DESC' }, ...props }}
      />
    </ProtegerPagina>
  );
};

export default List;
